import { Component } from '@theme/component';

const SELECT_EVENT = 'slideshow:select';

class ShoppableVideoSlide extends Component {
  /** @type {HTMLVideoElement | null} */
  video = null;

  connectedCallback() {
    super.connectedCallback();
    this.video = this.querySelector('video');

    if (this.video) {
      this.video.muted = true;
      this.video.pause();
    }

    this.#syncState();
  }

  activate() {
    if (!this.video) return;
    this.video.play().catch(() => {});
    this.#syncState();
  }

  deactivate() {
    if (!this.video) return;
    this.video.pause();
    this.#syncState();
  }

  /** @param {Event} event */
  toggleMute(event) {
    event.preventDefault();
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.#syncState();
  }

  /** @param {Event} event */
  togglePlay(event) {
    event.preventDefault();
    if (!this.video) return;

    if (this.video.paused) {
      this.video.play().catch(() => {});
    } else {
      this.video.pause();
    }

    this.#syncState();
  }

  #syncState() {
    this.classList.toggle('is-muted', Boolean(this.video?.muted));
    this.classList.toggle('is-paused', Boolean(this.video?.paused));
  }
}

class ShoppableVideoSliderComponent extends Component {
  /** @type {boolean} */
  #looped = false;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {Element | null} */
  #centeredSlide = null;

  /** @type {number} Width of one full (non-cloned) slide set, once looped. */
  #setWidth = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(SELECT_EVENT, this.#handleSelect);
    // Capture phase: DialogCloseEvent doesn't bubble, but capture listeners
    // still see it on the way down to whichever <dialog> dispatched it.
    document.addEventListener('dialog:close', this.#handleDialogClose, { capture: true });
    requestAnimationFrame(() => {
      // Activate the real initial slide first - cloning afterwards is safe
      // because activate()/deactivate() target that element instance directly,
      // regardless of where clones later get inserted around it.
      this.#activateInitialSlide();
      this.#setupInfiniteScroll();
      this.#setupCenteredSlideTracking();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(SELECT_EVENT, this.#handleSelect);
    document.removeEventListener('dialog:close', this.#handleDialogClose, { capture: true });
    this.#resizeObserver?.disconnect();
  }

  /**
   * Moves the scroller without the CSS scroll animation.
   *
   * slideshow-slides carries `scroll-behavior: smooth` (see
   * snippets/slideshow-styles.liquid), so a plain `scrollLeft = x` ANIMATES
   * towards the target over many frames - and every one of those frames fires
   * another scroll event, which re-enters the handlers below and can start the
   * next correction before the previous one has landed. That feeds itself and
   * the slider races through the videos.
   *
   * Every wrap correction in this component is a position bookkeeping step
   * that the visitor must never see, so all of them go through here.
   *
   * @param {HTMLElement} scroller
   * @param {number} left
   */
  #setScrollLeftInstantly(scroller, left) {
    const previousBehavior = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';
    scroller.scrollLeft = left;
    scroller.style.scrollBehavior = previousBehavior;
  }

  /**
   * iOS Safari can fail to repaint the GPU compositing layer for the scaled,
   * rounded-corner <video> elements in this slider after an unrelated
   * <dialog> elsewhere on the page closes (e.g. the quick-add modal) - the
   * symptom is solid black bars above/below the slider row that only clear
   * once the user taps the screen again (i.e. it's a stale paint, not a real
   * layout change). A transform-only nudge isn't forceful enough to make
   * Safari repaint reliably, and its compositor doesn't always settle into
   * the broken state immediately when the dialog's close animation ends -
   * so force a real reflow (toggling display) on each video's box, once
   * right away and once again shortly after to catch the delayed case too.
   */
  #handleDialogClose = () => {
    const repaint = () => {
      const mediaBoxes = this.querySelectorAll('.shoppable-video-slide__media');
      mediaBoxes.forEach((el) => {
        const previousDisplay = el.style.display;
        el.style.display = 'none';
        // Reading a layout property forces the display:none to actually take
        // effect before it's undone, instead of the browser coalescing both
        // style writes into a single no-op.
        void el.offsetHeight;
        el.style.display = previousDisplay;
      });
    };
    requestAnimationFrame(repaint);
    setTimeout(repaint, 150);
  };

  #activateInitialSlide() {
    const index = Number(this.querySelector('slideshow-component')?.getAttribute('initial-slide')) || 0;
    const slides = this.querySelectorAll('shoppable-video-slide');
    const initialSlide = slides[index] ?? slides[0];
    initialSlide?.activate();
    this.#centeredSlide = initialSlide ?? null;
    this.#centeredSlide?.classList.add('is-active-visual');
  }

  /** @param {CustomEvent<{slide: Element}>} event */
  #handleSelect = (event) => {
    this.querySelectorAll('shoppable-video-slide').forEach((slide) => slide.deactivate());

    const slide = event.detail?.slide;
    if (slide instanceof ShoppableVideoSlide) {
      slide.activate();
    } else {
      slide?.querySelector?.('shoppable-video-slide')?.activate();
    }
  };

  #setupInfiniteScroll() {
    const scroller = this.querySelector('slideshow-slides');
    if (!scroller) return;

    // The slider can still be laid out at zero width right after connect
    // (e.g. inside a hidden ancestor). Re-check whenever its size changes.
    this.#resizeObserver = new ResizeObserver(() => this.#tryLoop(/** @type {HTMLElement} */ (scroller)));
    this.#resizeObserver.observe(scroller);
  }

  /** @param {HTMLElement} scroller */
  #tryLoop(scroller) {
    if (this.#looped) return;
    if (scroller.clientWidth === 0) return;
    if (scroller.scrollWidth <= scroller.clientWidth + 1) return;

    this.#looped = true;
    this.#resizeObserver?.disconnect();

    const originalSlides = Array.from(scroller.children).filter(
      (el) => el.tagName === 'SHOPPABLE-VIDEO-SLIDE'
    );
    if (originalSlides.length === 0) return;

    // Clone the original set before and after itself, so there's always a
    // full copy to scroll into in either direction. Clones are inert and
    // hidden from assistive tech since they're purely visual duplicates -
    // their videos stay paused (same as any inactive slide) until scrolled in.
    const appendClone = (before) => {
      const fragment = document.createDocumentFragment();
      for (const slide of originalSlides) {
        const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
        clone.classList.remove('is-active-visual');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        fragment.appendChild(clone);
      }
      if (before) {
        scroller.prepend(fragment);
      } else {
        scroller.appendChild(fragment);
      }
    };

    appendClone(true);
    appendClone(false);

    // Measure the distance from a slide to its counterpart one set later,
    // rather than dividing scrollWidth by three. The scroller has a `gap`
    // between slides AND 18% inline padding (see
    // sections/shoppable-video-slider.liquid), neither of which divides evenly
    // into three sets - scrollWidth / 3 is off by roughly one gap, and that
    // error compounds with every wrap until the slides sit visibly off-centre
    // and the correction below re-fires endlessly.
    // slideshow-slides is position:relative, so offsetLeft is measured against
    // the scroller itself.
    const allSlides = Array.from(scroller.children).filter(
      (el) => el.tagName === 'SHOPPABLE-VIDEO-SLIDE'
    );
    const firstSlide = /** @type {HTMLElement} */ (allSlides[0]);
    const sameSlideOneSetLater = /** @type {HTMLElement} */ (allSlides[originalSlides.length]);
    const setWidth = sameSlideOneSetLater.offsetLeft - firstSlide.offsetLeft;
    if (setWidth <= 0) return;

    this.#setWidth = setWidth;
    this.#setScrollLeftInstantly(scroller, setWidth);

    let ticking = false;
    scroller.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (scroller.scrollLeft <= 0) {
          this.#setScrollLeftInstantly(scroller, scroller.scrollLeft + setWidth);
        } else if (scroller.scrollLeft >= setWidth * 2) {
          this.#setScrollLeftInstantly(scroller, scroller.scrollLeft - setWidth);
        }
        ticking = false;
      });
    });
  }

  /**
   * Tracks which slide is horizontally centered in the scroller and marks it
   * as the visual foreground slide (scaled up, elevated) while the rest sit
   * back as dimmed, scaled-down "background" slides - and plays that slide's
   * video, pausing the others.
   */
  #setupCenteredSlideTracking() {
    const scroller = this.querySelector('slideshow-slides');
    if (!scroller) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        this.#updateCenteredSlide(/** @type {HTMLElement} */ (scroller));
        ticking = false;
      });
    };

    scroller.addEventListener('scroll', onScroll);
    // Re-run after the infinite-scroll clones (if any) are inserted and the
    // scroll position jumps to the middle copy.
    requestAnimationFrame(() => this.#updateCenteredSlide(/** @type {HTMLElement} */ (scroller)));
  }

  /** @param {HTMLElement} scroller */
  #updateCenteredSlide(scroller) {
    const slides = Array.from(scroller.querySelectorAll('shoppable-video-slide'));
    if (!slides.length) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;

    let closest = /** @type {Element | null} */ (null);
    let closestDistance = Infinity;

    for (const slide of slides) {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - scrollerCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = slide;
      }
    }

    if (!closest) return;

    // Clones are inert (non-interactive) visual duplicates used purely to fill
    // the infinite-scroll illusion. Mid-swipe, before the edge-triggered wrap
    // correction in #tryLoop fires, the user can come to rest with a clone
    // centered instead of a real slide - identical to look at, but its
    // "Hinzufügen" button is unclickable because inert blocks all interaction.
    // Snap onto the real slide at the same position in the middle set instead
    // of ever marking a clone as active.
    if (closest.hasAttribute('inert') && this.#setWidth) {
      const closestIndex = slides.indexOf(closest);
      const isBeforeClone = closestIndex < slides.length / 3;
      const delta = isBeforeClone ? this.#setWidth : -this.#setWidth;
      this.#setScrollLeftInstantly(scroller, scroller.scrollLeft + delta);
      return; // The scroll event this triggers re-runs this method with the real slide centered.
    }

    if (closest === this.#centeredSlide) return;

    this.#centeredSlide?.classList.remove('is-active-visual');
    if (this.#centeredSlide instanceof ShoppableVideoSlide) {
      this.#centeredSlide.deactivate();
    }

    closest.classList.add('is-active-visual');
    if (closest instanceof ShoppableVideoSlide) {
      closest.activate();
    }

    this.#centeredSlide = closest;
  }
}

if (!customElements.get('shoppable-video-slide')) {
  customElements.define('shoppable-video-slide', ShoppableVideoSlide);
}

if (!customElements.get('shoppable-video-slider-component')) {
  customElements.define('shoppable-video-slider-component', ShoppableVideoSliderComponent);
}